import {
  CreateDraftDto,
  DraftModel,
  OrderLineUpdateDto,
  OrderType,
  OrderUpdateDto,
  OriginApp,
  type ValidatorResult,
} from '@onrunning/backstage-models';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useFormat } from '@/composables/format';
import { useNotification } from '@/composables/useNotification';
import { OrderEditType } from '@/constants/orderEditType';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { OrderAction } from '@/models/order-action.enum';
import { RouteNames } from '@/router';
import { DraftsService } from '@/services/drafts.service';
import { OrdersService } from '@/services/orders.service';
import { useAccountStore } from '@/stores/account';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { Draft } from '@/types/draft.interface';
import type { MatrixOrder } from '@/types/matrix.interface';
import type { EditableOrder, Order, OrderEntity } from '@/types/order.interface';
import { useFabricValidation } from './useFabricValidation';
import { useMatrixProduct } from './useMatrixProduct';
import { useNewRelic } from './useNewRelic';

export interface UseOrderManagement {
  contactId?: Ref<string | undefined>;
  editType?: OrderEditType;
}

export function useOrderManagement(options?: UseOrderManagement) {
  const userStore = useUserStore();
  const orderStore = useOrderStore();
  const accountStore = useAccountStore();
  const productMatrixStore = useProductMatrixStore();
  const { matrixType } = useMatrixProduct();
  const newrelic = useNewRelic();

  const fabricValidations = useFabricValidation(matrixType);

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const { notifyError } = useNotification();
  const { formatNonLocalisedDateString, formatError } = useFormat();

  // Services
  const ordersService = new OrdersService();
  const draftsService = new DraftsService();

  const saving = ref(false);
  const deleting = ref(false);
  const submitting = ref(false);
  const showProductsConfirmationModal = ref(false);

  const isOrderEditMode = computed(() => options?.editType === OrderEditType.Order);

  const isMultipleOrder = computed(() => orderStore.orderType === OrderType.MultiplePreOrder);

  const seasonStartDate = computed((): string => {
    return orderStore.orderTypeInfo?.startDate as string;
  });

  const orders = computed(() => {
    return new Map([...orderStore.orders, ...orderStore.updatedOrders]);
  });

  const seasonEndDate = computed((): string => {
    return orderStore.orderTypeInfo?.endDate as string;
  });

  const onSaveDraft = async () => {
    // Prevent duplicate draft creation from rapid clicks
    if (saving.value) {
      return;
    }

    saving.value = true;

    await upsertDrafts();
    saving.value = false;
    await router.push('/orders');
  };

  const deleteDraft = async (index: number) => {
    const draft = productMatrixStore.matrixOrders[index];

    if (draft?.orderId && draft.orderNumber) {
      await draftsService.remove(draft.orderId, userStore.selectedAccount?.id);
      orderStore.deleteOrder(draft.orderNumber);
    }

    productMatrixStore.deleteMatrixOrder(index);

    if (!productMatrixStore.matrixOrders.length) {
      return router.push('/orders');
    }
  };

  const deleteAllDraftsOnExit = async () => {
    deleting.value = true;

    try {
      await Promise.all(
        productMatrixStore.matrixOrders.map(async (draft) => {
          if (draft?.orderId) {
            await draftsService.remove(draft.orderId, userStore.selectedAccount?.id);
          }
        }),
      );
    } catch (err) {
      notifyError(t('backend.orders.delete_draft_error'));
    } finally {
      deleting.value = false;
      router.push('/orders');
    }
  };

  const onSubmitOrders = async () => {
    // Prevent double submission from rapid clicks
    if (submitting.value) {
      return;
    }

    if (accountStore.isOnHold) {
      onSaveDraft();

      return;
    }

    submitting.value = true;

    let confirmedOrderNumbers: string[] | undefined;

    if (isOrderEditMode.value) {
      confirmedOrderNumbers = await updateExistingOrder();
    } else {
      confirmedOrderNumbers = await submitDraft();
    }

    submitting.value = false;

    const confirmedOrderNumber = confirmedOrderNumbers?.join(', ');

    if (confirmedOrderNumber) {
      router.replace({
        name: RouteNames.SuccessOrdered,
        params: { orderType: orderStore.orderType, orderNumber: confirmedOrderNumber },
        query: { orderAction: isOrderEditMode.value ? OrderAction.Update : OrderAction.Create },
      });
    }
  };

  const onProceedToOrderDetails = async (autoProceeded: boolean) => {
    // Prevent duplicate draft creation from rapid clicks
    if (saving.value) {
      return;
    }

    try {
      if (autoProceeded) showProductsConfirmationModal.value = false;

      saving.value = true;

      if (isOrderEditMode.value) {
        const matrixOrder = productMatrixStore.matrixOrders[0];
        const order = orders.value.get(matrixOrder.orderNumber as string);
        const payload = {
          ...order,
          products: buildUpdateOrderProducts(matrixOrder),
        } as EditableOrder;

        orderStore.setUpdatedOrder(payload);
        orderStore.setUpdateStatus(productMatrixStore.matrixUpdated);
      } else {
        const promises = await upsertDrafts();

        promises?.forEach((draft, index) => {
          orderStore.setOrder(draft);
          productMatrixStore.updateMatrixOrder({ orderId: draft.id, orderNumber: draft.orderNumber }, index);
        });

        productMatrixStore.setUpdateStatus(false);
      }

      const firstMatrixOrder = productMatrixStore.matrixOrders[0];
      if (firstMatrixOrder.orderId) {
        const path = `/orders/${firstMatrixOrder.orderId}/edit-products/${OrderEditType.Draft}/${AddEditOrderBreadcrumbSteps.orderDetails}`;
        router.push(path);

        return;
      }

      router.push({
        params: {
          ...route.params,
          step: AddEditOrderBreadcrumbSteps.orderDetails,
        },
      });
    } catch (error) {
      newrelic.noticeError(error as Error, {
        message: 'onProceedToOrderDetails failed',
      });
    } finally {
      saving.value = false;
      showProductsConfirmationModal.value = false;
    }
  };

  const buildCreateDraftLines = (matrixOrder: MatrixOrder) => {
    const draftProducts = [];

    for (const productId in matrixOrder.matrixProducts) {
      const product = productMatrixStore.products[productId];
      if (!product) {
        continue;
      }

      const disabledReason = fabricValidations.getFabricDisabledReason(
        product.launchDate,
        matrixOrder.date,
        seasonStartDate.value,
      );

      if (!disabledReason) {
        const matrixProduct = matrixOrder.matrixProducts[productId];

        if (matrixProduct.quantity) {
          draftProducts.push({
            barcode: product.productId,
            quantity: matrixProduct.quantity,
            productGroupName: product.productGroupName,
            productName: `${product.productName} ${product.sizeId}`,
            itemNumber: product.productGroupNumber,
          });
        }
      }
    }

    return draftProducts;
  };

  const buildUpdateDraftProducts = (matrixOrder: MatrixOrder) => {
    const draftProducts = [];

    for (const productId in matrixOrder.matrixProducts) {
      const product = productMatrixStore.products[productId];
      if (!product) {
        continue;
      }

      const disabledReason = fabricValidations.getFabricDisabledReason(
        product.launchDate,
        matrixOrder.date,
        seasonStartDate.value,
      );

      if (!disabledReason) {
        const matrixProduct = matrixOrder.matrixProducts[productId];

        if (matrixProduct.quantity) {
          draftProducts.push({
            quantity: matrixProduct.quantity,
            productId: matrixProduct.productId ?? product.productId,
            name: matrixProduct.productGroupNumber ?? product.productGroupNumber,
            productGroupName: matrixProduct.productGroupName ?? product.productGroupName,
            description: `${product.productName} ${product.sizeId}`,
          });
        }
      }
    }

    return draftProducts;
  };

  const buildSubmitDraftProducts = (draft: Draft) => {
    return draft.products.map((product) => ({
      productId: product.productId,
      quantity: product.quantity,
      description: product.description,
      productGroupName: product.productGroupName,
    }));
  };

  const buildUpdateOrderProducts = (matrixOrder: MatrixOrder) => {
    const orderProducts = [];

    for (const productId in matrixOrder.matrixProducts) {
      const product = productMatrixStore.products[productId];

      if (!product) {
        continue;
      }

      const disabledReason = fabricValidations.getFabricDisabledReason(
        product.launchDate,
        matrixOrder.date,
        seasonStartDate.value,
      );

      if (!disabledReason) {
        const matrixProduct = matrixOrder.matrixProducts[productId];

        if (matrixProduct.quantity) {
          orderProducts.push({
            name: product.productGroupNumber,
            productSizeId: product.sizeId,
            productId: product.productId,
            quantity: matrixProduct.quantity,
            productGroupName: product.productGroupName,
            description: `${product.productName} ${product.sizeId}`,
          });
        }
      }
    }

    return orderProducts;
  };

  const upsertDrafts = async () => {
    try {
      let groupId: string | undefined;

      if (isMultipleOrder.value) {
        groupId = orderStore.orders.values().next().value?.groupId || uuidv4();
      }

      const promises = await Promise.all(
        productMatrixStore.matrixOrders.map(async (matrixOrder) => {
          if (!matrixOrder.orderId) {
            const createdDraft = await createDraft(matrixOrder, groupId);

            return createdDraft;
          }

          const update = orders.value.get(matrixOrder.orderNumber as string) as Draft;

          const updatedDraft = await updateDraft(update, matrixOrder);

          return updatedDraft;
        }),
      );

      return promises;
    } catch (e: any) {
      notifyError(e?.message || t('backend.orders.save_draft_error'));
      throw e;
    }
  };

  const createDraft = async (matrixOrder: MatrixOrder, groupId?: string): Promise<Draft> => {
    const draft: CreateDraftDto = {
      origin: OriginApp.Backstage,
      contactId: resolveContactId(options?.contactId?.value),
      type: orderStore.orderType as OrderType,
      requestedDate: formatNonLocalisedDateString(matrixOrder.date),
      isSalesRep: userStore.isASalesRep,
      groupId,
      lines: buildCreateDraftLines(matrixOrder),
      customerPoNumber: matrixOrder.customerPoNumber,
    };

    if (userStore.isASalesRep && !draft.contactId) {
      draft.contactId = orderStore.orders.values().next().value?.contactId;
    }

    const result: any = await draftsService.createDraft(draft, userStore.selectedAccount?.id);

    if (!result.isValid()) {
      newrelic.noticeError(new Error('Draft creation failed'), {
        isSalesRep: draft.isSalesRep,
        groupId: draft.groupId,
        contactId: draft.contactId,
      });
      throw new Error(formatError(result.errors) || t('global.error_creating_draft'));
    }

    return result.model;
  };

  const updateDraft = async (draft: Draft, matrixOrder: MatrixOrder): Promise<Draft> => {
    const updatedDraft = {
      id: draft.id,
      orderNumber: draft.orderNumber,
      contactId: resolveContactId(draft.contactId),
      accountId: userStore.selectedAccount.id,
      userId: userStore.userProfile.user.id,
      type: orderStore.orderType,
      customerPoNumber: draft.customerPoNumber,
      deliveryAddress: draft.deliveryAddress,
      dueDate: formatNonLocalisedDateString(matrixOrder.date),
      products: buildUpdateDraftProducts(matrixOrder),
    } as any;

    const result = await draftsService.update(updatedDraft, userStore.selectedAccount?.id);

    if (!result.isValid()) {
      newrelic.noticeError(new Error('Draft update failed'), {
        accountNumber: updatedDraft.accountId,
        userId: updatedDraft.userId,
        contactId: updatedDraft.contactId,
      });
      throw new Error(formatError(result.errors) || t('global.error_saving_draft'));
    }

    return { ...draft, ...updatedDraft } as Draft;
  };

  const updateExistingOrder = async (): Promise<string[] | undefined> => {
    const updates = Array.from(orderStore.updatedOrders.values()) as EditableOrder[];
    try {
      return await Promise.all(
        updates.map(async (order: EditableOrder) => {
          const payload = {
            orderNumber: order.orderNumber,
            contactId: resolveContactId(order.contactId),
            deliveryAddress: order.deliveryAddress,
            customerPoNumber: order.customerPoNumber,
            lines: order.products.map((product) => ({
              barcode: product.productId,
              quantity: product.quantity,
            })) as OrderLineUpdateDto[],
          } as OrderUpdateDto;

          const result = await ordersService.updateOrder(payload, userStore.selectedAccount?.id);

          if (!result.isValid()) {
            throw new Error(formatError(result.errors) || t('global.error_updating_order'));
          }

          return order.orderNumber;
        }),
      );
    } catch (err: any) {
      notifyError(err?.message || t('global.error_updating_order'));
    }
  };

  const submitDraft = async (): Promise<string[] | undefined> => {
    const drafts = Array.from(orders.value.values()) as Draft[];

    try {
      return await Promise.all(
        drafts.map(async (draft: Draft) => {
          const payload = {
            id: draft.id,
            orderNumber: draft.orderNumber,
            contactId: resolveContactId(draft.contactId),
            customerPoNumber: draft.customerPoNumber,
            accountId: userStore.selectedAccount.id,
            userId: userStore.userProfile.user.id,
            type: draft.type,
            dueDate: draft.dueDate,
            deliveryAddress: draft.deliveryAddress,
            products: buildSubmitDraftProducts(draft),
          } as DraftModel;

          const result = await draftsService.submit(payload, userStore.selectedAccount?.id);

          if (!result.isValid() || !result.model) {
            throw new Error(formatError(result.errors) || t('global.error_submitting_draft'));
          }

          return result.model.orderNumber;
        }),
      );
    } catch (err: any) {
      notifyError(err?.message || t('global.error_submitting_draft'));
    }
  };

  const getOrders = async (orderId: string, isDraft = false): Promise<Draft[] | EditableOrder[] | Order[]> => {
    const order = await fetchOrder(orderId, isDraft);

    let relatedOrders: Draft[] | EditableOrder[] | Order[] = [];

    if (order?.relatedOrders?.length) {
      relatedOrders = await loadRelatedOrders(order.relatedOrders, order.id, isDraft);
    }

    return [order, ...relatedOrders] as Draft[] | EditableOrder[] | Order[];
  };

  const fetchOrder = async (id: string, isDraft: boolean = false): Promise<OrderEntity> => {
    if (isDraft) {
      const result: ValidatorResult<any> = await draftsService.get(id, userStore.selectedAccount?.id, true);
      if (!result.isValid()) {
        throw new Error(formatError(result.errors) || t('orders.fetch_draft_error'));
      }

      return result.model as Draft;
    } else {
      const result: ValidatorResult<any> = await ordersService.get(id, userStore.selectedAccount?.id);
      if (!result.isValid()) {
        throw new Error(formatError(result.errors) || t('orders.fetch_order_error'));
      }

      return result.model as EditableOrder | Order;
    }
  };

  const loadRelatedOrders = async (
    relatedOrders: { id: string }[],
    mainOrderId: string,
    isDraft: boolean,
  ): Promise<Draft[] | EditableOrder[] | Order[]> => {
    const requests = [];

    for (const order of relatedOrders) {
      if (order.id !== mainOrderId) {
        requests.push(fetchOrder(order.id, isDraft));
      }
    }

    return Promise.all(requests) as Promise<Draft[] | EditableOrder[] | Order[]>;
  };

  const resolveContactId = (contactId?: string): string | undefined => {
    if (userStore.isADealer) {
      return undefined;
    }

    if (contactId?.trim()) {
      return contactId.trim();
    }

    return undefined;
  };

  return {
    submitting,
    saving,
    deleting,
    showProductsConfirmationModal,
    onSaveDraft,
    updateDraft,
    deleteDraft,
    deleteAllDraftsOnExit,
    onSubmitOrders,
    onProceedToOrderDetails,
    seasonStartDate,
    seasonEndDate,
    getOrders,
  };
}
